import React, { useState, useRef, useEffect } from 'react';
import { useReactFlow, applyNodeChanges } from '@xyflow/react';
import GenAiChat from 'js/components/elements/GenAiChat/GenAiChat';
import useEntities from 'new/state/entities/repository/useEntities';

const GenAiChatNode = ({ id, data, nexsetId, position, isFlowExpanded }) => {
  const { setNodes } = useReactFlow();
  const [nodePosition, setNodePosition] = useState(position);
  const [dragging, setDragging] = useState(false);
  const nodeRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef<number>(0);
  const startYRef = useRef<number>(0);
  const startLeftRef = useRef<number>(0);
  const startTopRef = useRef<number>(0);

  const { data: selectedNexsetData } = useEntities(`dataSets[${nexsetId}]`);

  const nexsetName = selectedNexsetData?.name;

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (dragging && nodeRef.current) {
        // Calculate the new position based on mouse movement
        const deltaX = event.clientX - startXRef.current;
        const deltaY = event.clientY - startYRef.current;

        let newLeft = startLeftRef.current + deltaX;
        let newTop = startTopRef.current + deltaY;

        // Apply boundary constraints
        const rightBoundary = window.innerWidth - nodeRef.current.offsetWidth;
        const bottomBoundary = window.innerHeight - nodeRef.current.offsetHeight;

        // Clamp the position within boundaries
        newLeft = Math.max(0, Math.min(newLeft, rightBoundary));
        newTop = Math.max(0, Math.min(newTop, bottomBoundary));

        const newPosition = {
          x: newLeft + 150, // Add back the offset used in style
          y: newTop + 100, // Add back the offset used in style
        };

        setNodePosition(newPosition);

        setNodes((nds) =>
          applyNodeChanges(
            [
              {
                id,
                type: 'position',
                position: newPosition,
              },
            ],
            nds,
          ),
        );
      }
    };

    const handleMouseUp = () => {
      setDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, setNodes, id]);

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!nodeRef.current) return;

    // Capture initial mouse position
    startXRef.current = event.clientX;
    startYRef.current = event.clientY;

    // Capture initial element position (accounting for the style offsets)
    startLeftRef.current = nodePosition.x - 150;
    startTopRef.current = nodePosition.y - 100;

    setDragging(true);
  };

  return (
    <GenAiChat
      ref={nodeRef}
      onClose={data.onClose}
      onStreamComplete={data.onStreamComplete}
      nexsetId={nexsetId}
      nexsetName={nexsetName}
      withNexsetBadge
      noCodeInteraction
      onMouseDown={handleMouseDown}
      selectedNexsetData={selectedNexsetData}
      style={{
        zIndex: 1000,
        position: 'absolute',
        top: nodePosition.y - 100,
        left: nodePosition.x - 150,
        maxWidth: isFlowExpanded ? '800px' : '600px',
        maxHeight: isFlowExpanded ? '660px' : '485px',
      }}
    />
  );
};

export default GenAiChatNode;
